import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { MentionBoostPlacementMode, type MentionBoostConfigSnapshot } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface BoostAppliedPlacementProps {
  config: MentionBoostConfigSnapshot;
}

/**
 * Read-only view of the placement frozen at launch: which mode was used, and
 * which campaign hosts the Fluenceur ad set. Read from the snapshot, never
 * recomputed, so what is shown is what actually ran. Legacy snapshots carry no
 * placement and are shown as the default new-campaign mode.
 */
export function BoostAppliedPlacement({ config }: BoostAppliedPlacementProps) {
  const { t } = useTranslation();

  const isExistingCampaign = config.placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN;

  return (
    <Box mt={5} border="1px solid" borderColor="border.muted" borderRadius="md" px={3} py={3}>
      <Text
        fontSize="2xs"
        fontWeight="medium"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
      >
        {t('mentions.boost.detail.placementTitle')}
      </Text>
      <VStack align="stretch" gap={1}>
        <Text fontSize="xs">
          {t(
            isExistingCampaign
              ? 'mentions.boost.detail.placementExistingCampaign'
              : 'mentions.boost.detail.placementNewCampaign'
          )}
        </Text>
        {isExistingCampaign && config.parentCampaignName && (
          <HStack gap={2} align="baseline">
            <Text w="140px" flexShrink={0} fontSize="xs" color="fg.muted" truncate>
              {t('mentions.boost.detail.placementCampaignLabel')}
            </Text>
            <Text flex={1} minW={0} fontSize="xs" wordBreak="break-word">
              {config.parentCampaignName}
            </Text>
          </HStack>
        )}
        {isExistingCampaign && config.campaignBudgetManaged && (
          <Text fontSize="xs" color="fg.muted">
            {t('mentions.boost.detail.placementCampaignBudget')}
          </Text>
        )}
      </VStack>
    </Box>
  );
}
